import { clear, h } from '../core/dom'
import { haptic } from '../core/haptics'
import { go } from '../core/router'
import { getNight, nightKey, updateNight, type SymptomKey } from '../core/store'
import { hasRedFlag, RED_FLAG_SYMPTOMS } from '../core/safety'
import { num, t } from '../i18n'
import { topbar } from './shared'

const SYMPTOM_ORDER: SymptomKey[] = [
  'anxiety',
  'racingHeart',
  'dread',
  'muscleTension',
  'tremor',
  'sweating',
  'nausea',
  'headache',
  'tinnitus',
  'lightSensitivity',
  'soundSensitivity',
  'derealization',
  // Red flags sit at the end, visually separated, and tapping one interrupts
  // the check-in with the emergency panel rather than just recording it.
  'seizure',
  'hallucination',
  'confusion',
  'fever',
]

const HOURS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]

export function renderCheckin(root: HTMLElement): void {
  // A check-in in the morning is about the night that just ended, so it edits
  // yesterday's row rather than opening a new one.
  const key = nightKey(new Date(Date.now() - 6 * 3600_000))
  const existing = getNight(key)

  let sleptHours = existing.sleptHours ?? 5
  let quality = existing.quality ?? 3
  let woke = existing.wokeInNight ?? false
  let symptoms = new Set<SymptomKey>(existing.symptoms)
  let note = existing.note ?? ''

  const paint = (): void => {
    clear(root)
    const d = t()

    const hoursRow = h(
      'div',
      { class: 'chips' },
      ...HOURS.map((hrs) =>
        h(
          'button',
          {
            class: `chip ${sleptHours === hrs ? 'on' : ''}`,
            onclick: () => {
              haptic.tap()
              sleptHours = hrs
              paint()
            },
          },
          hrs === 9 ? `${num(9)}+` : num(hrs),
        ),
      ),
    )

    const qualityRow = h(
      'div',
      { class: 'chips' },
      ...([1, 2, 3, 4, 5] as const).map((q) =>
        h(
          'button',
          {
            class: `chip ${quality === q ? 'on' : ''}`,
            onclick: () => {
              haptic.tap()
              quality = q
              paint()
            },
          },
          [d.checkin.q1, d.checkin.q2, d.checkin.q3, d.checkin.q4, d.checkin.q5][q - 1]!,
        ),
      ),
    )

    const wokeRow = h(
      'div',
      { class: 'chips' },
      ...([true, false] as const).map((v) =>
        h(
          'button',
          {
            class: `chip ${woke === v ? 'on' : ''}`,
            onclick: () => {
              haptic.tap()
              woke = v
              paint()
            },
          },
          v ? d.checkin.yes : d.checkin.no,
        ),
      ),
    )

    const symptomRow = h(
      'div',
      { class: 'chips' },
      ...SYMPTOM_ORDER.map((s) => {
        const isRedFlag = RED_FLAG_SYMPTOMS.includes(s)
        return h(
          'button',
          {
            class: `chip ${isRedFlag ? 'redflag' : ''} ${symptoms.has(s) ? 'on' : ''}`,
            onclick: () => {
              haptic.tap()
              if (symptoms.has(s)) symptoms.delete(s)
              else symptoms.add(s)
              if (isRedFlag && symptoms.has(s)) {
                // Save what we have, then get out of the way.
                save()
                haptic.warn()
                go('safety')
                return
              }
              paint()
            },
          },
          d.symptoms[s],
        )
      }),
    )

    const noteInput = h('textarea', {
      rows: 3,
      value: note,
      placeholder: `${d.ledger.note} (${d.common.optional})`,
      oninput: (e: Event) => {
        note = (e.target as HTMLTextAreaElement).value
      },
    })

    root.append(
      topbar(d.checkin.title),
      h(
        'div',
        { class: 'stack-lg fade-in' },
        h('div', { class: 'stack-sm' }, h('label', { class: 'field' }, d.checkin.howLong), hoursRow),
        h('div', { class: 'stack-sm' }, h('label', { class: 'field' }, d.checkin.quality), qualityRow),
        h('div', { class: 'stack-sm' }, h('label', { class: 'field' }, d.checkin.woke), wokeRow),
        h(
          'div',
          { class: 'stack-sm' },
          h('label', { class: 'field' }, d.checkin.symptomsQ),
          symptomRow,
        ),
        h('div', { class: 'stack-sm' }, h('label', { class: 'field' }, d.ledger.note), noteInput),
        h(
          'button',
          {
            class: 'btn primary block',
            onclick: () => {
              haptic.complete()
              save()
              go('ledger')
            },
          },
          d.common.save,
        ),
      ),
    )
  }

  const save = (): void => {
    updateNight(
      {
        sleptHours,
        quality,
        wokeInNight: woke,
        symptoms: [...symptoms],
        note: note.trim() || undefined,
      },
      key,
    )
  }

  paint()

  // Belt and braces: if a red flag ever lands in the record without going
  // through the tap handler, the safety screen still wins.
  if (hasRedFlag(existing.symptoms)) go('safety')
}
